import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
    Box,
    Button,
    MenuItem,
    Paper,
    Select,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
} from '@mui/material';
import { StocktakeService } from '../../services/stocktake.service';
import {
    StocktakeDTO,
    StocktakeCountDTO,
    STOCKTAKE_STATUS_LABELS,
} from '../../interfaces/stocktake.interface';
import { SegmentService } from '../../services/segment.service';
import { canReadSegments } from '../../constants/segmentRoles';
import { AuthService } from '../../services/auth.service';
import { Segment } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

interface CountInput {
    countedQty: string;
    unitCost: string;
    segmentId: string;
}

const StocktakeDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    // SegmentController reads are Accountant/InstitutionAdmin; for other roles hide the picker instead of 403ing.
    const showSegments = canReadSegments(AuthService.getCurrentUser()?.roles);
    const [stocktake, setStocktake] = useState<StocktakeDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionInProgress, setActionInProgress] = useState<boolean>(false);
    const [counts, setCounts] = useState<Record<string, CountInput>>({});
    const [segments, setSegments] = useState<Segment[]>([]);

    const canManage = usePermission(PERMISSIONS.MENU_ITEMS, true);

    const initCounts = (data: StocktakeDTO) => {
        const initial: Record<string, CountInput> = {};
        (data.lines ?? []).forEach((line) => {
            initial[line.id] = {
                countedQty: line.countedQty != null ? String(line.countedQty) : '',
                unitCost: line.unitCost != null ? String(line.unitCost) : '',
                segmentId: line.segmentId ?? '',
            };
        });
        setCounts(initial);
    };

    const fetchStocktake = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await StocktakeService.getStocktakeById(id);
            setStocktake(data);
            initCounts(data);
        } catch (err) {
            console.error('Error fetching stocktake:', err);
            toast.showApiError(err, 'Failed to load stocktake');
        } finally {
            setLoading(false);
        }
    }, [id, toast]);

    useEffect(() => {
        fetchStocktake();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!showSegments) return;
        SegmentService.getByInstitution(0, 200)
            .then((page: { content: Segment[] }) => {
                setSegments((page.content ?? []).filter((s) => s.active !== false));
            })
            .catch((err: unknown) => {
                console.error('Failed to load segments', err);
            });
    }, []);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handleCountChange = (lineId: string, field: keyof CountInput, value: string) => {
        setCounts((prev) => ({
            ...prev,
            [lineId]: { ...prev[lineId], [field]: value },
        }));
    };

    const handleSubmitCounts = async () => {
        if (!id || !stocktake) return;
        const payload: StocktakeCountDTO[] = [];
        for (const line of stocktake.lines ?? []) {
            const input = counts[line.id];
            if (!input || input.countedQty.trim() === '') continue;
            const qty = Number(input.countedQty);
            if (Number.isNaN(qty)) {
                toast.showError(`Invalid counted quantity for ${line.itemName}`);
                return;
            }
            const entry: StocktakeCountDTO = { lineId: line.id, countedQty: qty };
            if (input.unitCost.trim() !== '') {
                const cost = Number(input.unitCost);
                if (Number.isNaN(cost)) {
                    toast.showError(`Invalid unit cost for ${line.itemName}`);
                    return;
                }
                entry.unitCost = cost;
            }
            if (input.segmentId) {
                entry.segmentId = input.segmentId;
            }
            payload.push(entry);
        }
        if (payload.length === 0) {
            toast.showError('Enter at least one counted quantity');
            return;
        }
        try {
            setActionInProgress(true);
            const updated = await StocktakeService.submitCounts(id, payload);
            setStocktake(updated);
            initCounts(updated);
            toast.showSuccess('Counts submitted');
        } catch (err) {
            console.error('Error submitting counts:', err);
            toast.showApiError(err, 'Failed to submit counts');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleApprove = async () => {
        if (!id) return;
        try {
            setActionInProgress(true);
            const updated = await StocktakeService.approveStocktake(id);
            setStocktake(updated);
            initCounts(updated);
            toast.showSuccess('Stocktake approved');
        } catch (err) {
            console.error('Error approving stocktake:', err);
            toast.showApiError(err, 'Failed to approve stocktake');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleCancel = async () => {
        if (!id) return;
        try {
            setActionInProgress(true);
            const updated = await StocktakeService.cancelStocktake(id);
            setStocktake(updated);
            initCounts(updated);
            toast.showSuccess('Stocktake cancelled');
        } catch (err) {
            console.error('Error cancelling stocktake:', err);
            toast.showApiError(err, 'Failed to cancel stocktake');
        } finally {
            setActionInProgress(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!stocktake) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Stocktake not found.</Typography>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/stocktakes')}>
                    Back to List
                </Button>
            </Box>
        );
    }

    const isDraft = stocktake.status === 'DRAFT';
    const isPendingApproval = stocktake.status === 'PENDING_APPROVAL';
    const editable = isDraft && canManage;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Stocktake {stocktake.referenceNo}</Typography>
                <Button variant="outlined" onClick={() => navigate('/stocktakes')}>
                    Back to List
                </Button>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Store</Typography>
                        <Typography>{stocktake.storeName || '-'}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Typography>
                            {STOCKTAKE_STATUS_LABELS[stocktake.status] ?? stocktake.status}
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Stocktake Date</Typography>
                        <Typography>{formatDate(stocktake.stocktakeDate)}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Posted Date</Typography>
                        <Typography>{formatDate(stocktake.postedDate)}</Typography>
                    </Box>
                </Box>
                {stocktake.notes && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography>{stocktake.notes}</Typography>
                    </Box>
                )}
            </Paper>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell>#</TableCell>
                            <TableCell>Item Code</TableCell>
                            <TableCell>Item Name</TableCell>
                            <TableCell align="right">System Qty</TableCell>
                            <TableCell align="right">Counted Qty</TableCell>
                            <TableCell align="right">Unit Cost</TableCell>
                            <TableCell align="right">Variance</TableCell>
                            {editable && showSegments && <TableCell>Segment</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(stocktake.lines ?? []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={editable ? (showSegments ? 8 : 7) : 7} align="center">
                                    No lines
                                </TableCell>
                            </TableRow>
                        ) : (
                            (stocktake.lines ?? []).map((line, index) => (
                                <TableRow key={line.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{line.itemCode}</TableCell>
                                    <TableCell>{line.itemName}</TableCell>
                                    <TableCell align="right">{line.systemQty}</TableCell>
                                    <TableCell align="right">
                                        {editable ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={counts[line.id]?.countedQty ?? ''}
                                                onChange={(e) =>
                                                    handleCountChange(line.id, 'countedQty', e.target.value)
                                                }
                                                sx={{ width: 110 }}
                                            />
                                        ) : (
                                            line.countedQty ?? '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {editable ? (
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={counts[line.id]?.unitCost ?? ''}
                                                onChange={(e) =>
                                                    handleCountChange(line.id, 'unitCost', e.target.value)
                                                }
                                                sx={{ width: 110 }}
                                            />
                                        ) : (
                                            line.unitCost ?? '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{line.variance}</TableCell>
                                    {editable && showSegments && (
                                        <TableCell>
                                            <Select
                                                size="small"
                                                displayEmpty
                                                value={counts[line.id]?.segmentId ?? ''}
                                                onChange={(e) =>
                                                    handleCountChange(line.id, 'segmentId', e.target.value)
                                                }
                                                sx={{ width: 180 }}
                                            >
                                                <MenuItem value="">(default)</MenuItem>
                                                {segments.map((seg) => (
                                                    <MenuItem key={seg.id} value={seg.id ?? ''}>
                                                        {seg.code} — {seg.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2 }}>
                {isDraft && canManage && (
                    <Button
                        variant="contained"
                        onClick={handleSubmitCounts}
                        disabled={actionInProgress}
                    >
                        Submit Counts
                    </Button>
                )}
                {isPendingApproval && canManage && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleApprove}
                        disabled={actionInProgress}
                    >
                        Approve
                    </Button>
                )}
                {(isDraft || isPendingApproval) && canManage && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancel}
                        disabled={actionInProgress}
                    >
                        Cancel Stocktake
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default StocktakeDetails;
